import { useEffect, useRef, useState } from "react";
import { debounceWithPromise } from "../helpers/function";
import logger from "../helpers/logger";
import AuthService from "../services/auth-service";
import { useCurrentUser } from "../store/current-user";

const MAX_RETRIES = 3;
const refreshAccessToken = debounceWithPromise(AuthService.refreshAccessToken, 1000);

export default function useInitializeApp() {
  const [error, setError] = useState<string|null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("Initializing application...");
  const [, , fetchCurrentUser] = useCurrentUser();

  // useRef preserves the value without triggering unwanted re-renders
  const retryCount = useRef(0);

  useEffect(() => { 
    initializeApp();
  }, []);

  // Network Event Listener Effect
  useEffect(() => {
    function handleInternetConnectionRestored() { 
      setIsOffline(false);

      // Auto-retry initialization if an error state is currently active
      if(error && retryCount.current < MAX_RETRIES) {
        console.log("Internet restored. Auto-retrying initialization...");
        initializeApp(true);
      }
    }

    function handleInternetConnectionLost() {
      setIsOffline(true);
      setError("Network connection lost. Please check your internet connection.");
    }

    // Bind browser network events
    window.addEventListener("online", handleInternetConnectionRestored);
    window.addEventListener("offline", handleInternetConnectionLost);

    return () => {
      window.removeEventListener("online", handleInternetConnectionRestored);
      window.removeEventListener("offline", handleInternetConnectionLost);
    };
  }, [error]);

  // Reset retry counters manually if explicit user interaction happens
  function handleManualRetry() {
    retryCount.current = 0; // Clear bounds because user explicitly clicked
    initializeApp(false);
  }

  async function initializeApp(isAutoRetry = false) { 
    if(!navigator.onLine) {
      setIsOffline(true);
      setError("No internet connection detected. Check your network hardware.");
      return;
    }

    setError(null); // Reset errors on initialization attempt
    setProgress(0);
    setStatusMessage("Initializing application...");

    try { 
      // `refreshAccessToken()` must be called before `fetchCurrentUser()`.
      // This ensures that the access token has been refreshed 
      // before attempting to fetch the user.

      setProgress(15);
      setStatusMessage("Checking active user session...");
      const userStillLoggedIn = await refreshAccessToken();

      setProgress(50);

      if(userStillLoggedIn) { 
        setStatusMessage("User session is active. Fetching user information...");
        await fetchCurrentUser();
      } else {
        setStatusMessage("No active user sessions found.");
      }

      setProgress(80); 
      setStatusMessage("Finalizing setup...");

      setProgress(100);
      setIsInitializing(false);
      setStatusMessage(`Welcome to ${process.env.APP_NAME ?? "NearBuyCart"}!`);
      retryCount.current = 0; // Clear retry limits on a successful boot
    } catch(error) {
      // Check if browser is offline OR if fetch failed completely due to CORS/DNS/Network
      if(!navigator.onLine || error.name === 'TypeError' || error.message.includes('fetch')) {
        setIsOffline(true);
        setError("Network connection lost. Please check your internet connection.");
      } else {
        setIsOffline(false);
        
        if(error.name === "Pending Promise Cancelled" && 
          error.message === "Pending Promise Resolution Cancelled by a newer debounced call") {
          logger.error("Debounced `refreshAccessToken` error: ", error);
        } else  { 
          logger.error("Initialization failed: ", error);
          setStatusMessage("Initialization failed");
          setError(error.message || "A network connectivity issue occurred.");
        }
      }

      // Handle loop capping
      if(isAutoRetry) {
        retryCount.current += 1;
        logger.warn(`Auto-retry attempt ${retryCount.current} of ${MAX_RETRIES}`);
      }
    }
  }

  return { 
    error, 
    isInitializing, 
    isOffline, 
    progress, 
    statusMessage, 
    maxRetries : MAX_RETRIES, 
    retryCount : retryCount.current,
    restart    : handleManualRetry,
  };
};
